<template>
  <div class="relative min-h-screen overflow-hidden">
    <!-- 背景装饰 -->
    <div class="fixed inset-0 bg-gradient-to-br from-primary-500/10 via-purple-500/5 via-blue-500/5 to-gray-50/95 bg-gradient-animate"></div>

    <!-- 增强粒子背景 -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div v-for="i in 20" :key="i" 
           class="particle absolute rounded-full"
           :class="i % 3 === 0 ? 'w-2 h-2 bg-primary-400/30' : i % 3 === 1 ? 'w-1.5 h-1.5 bg-purple-400/25' : 'w-1 h-1 bg-blue-400/20'"
           :style="{
             left: Math.random() * 100 + '%',
             top: Math.random() * 100 + '%',
             animationDelay: Math.random() * 8 + 's',
             animationDuration: (Math.random() * 15 + 20) + 's'
           }">
      </div>
    </div>

    <!-- 装饰背景光晕 -->
    <div class="fixed top-0 left-0 w-[900px] h-[900px] bg-gradient-to-r from-primary-500/20 to-purple-400/15 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2 animate-morph"></div>
    <div class="fixed bottom-0 right-0 w-[800px] h-[800px] bg-gradient-to-tl from-primary-400/15 via-pink-300/10 to-purple-300/10 rounded-full blur-[90px] translate-x-1/3 translate-y-1/3 animate-morph animation-delay-4000"></div>
    <div class="fixed top-1/2 left-1/2 w-[600px] h-[600px] bg-gradient-radial from-blue-400/10 to-transparent rounded-full blur-[80px] -translate-x-1/2 -translate-y-1/2 animate-pulse-glow"></div>

    <!-- 主要内容 -->
    <div class="relative">
      <LandingHeader />

      <!-- Hero区域 - 增强动画效果 -->
      <section class="relative pt-24 pb-20 overflow-hidden">
        <div class="container mx-auto px-4 text-center relative">
          <div class="hero-content">
            <!-- 标签徽章 -->
            <div class="mb-8 animate-fade-in-down">
              <div class="inline-flex items-center px-5 py-2.5 bg-white/80 backdrop-blur-sm border border-primary-200/50 rounded-full text-primary-600 text-sm font-medium shadow-lg shadow-primary-500/10 hover:shadow-xl hover:shadow-primary-500/20 transition-all duration-500 cursor-default group">
                <span class="w-2 h-2 bg-primary-500 rounded-full mr-3 animate-ping-slow"></span>
                <span class="group-hover:tracking-wide transition-all duration-300">🚀 一键掌握AI智能体平台</span>
              </div>
            </div>
            
            <!-- 主标题 - 带打字动画 -->
            <h1 class="text-4xl md:text-5xl lg:text-7xl font-bold mb-8 tracking-tight leading-tight">
              <span class="inline-block animate-fade-in-up animation-delay-200">
                <span class="text-primary-500 relative inline-block hover:scale-105 transition-transform duration-300">
                  <span class="relative z-10">{{ $t('landing.hero.highlight') }}</span>
                  <span class="absolute -bottom-2 left-0 w-full h-3 bg-primary-200/50 -skew-x-6 transform"></span>
                </span>
              </span>
              <br class="md:hidden">
              <span class="inline-block animate-fade-in-up animation-delay-400">
                <span class="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-primary-800 to-gray-900 bg-size-200 animate-gradient-x">
                  {{ $t('landing.hero.subtitle') }}
                </span>
              </span>
            </h1>

            <!-- 描述文字 -->
            <p class="text-gray-600 mb-12 max-w-2xl mx-auto text-lg md:text-xl leading-relaxed animate-fade-in-up animation-delay-600">
              {{ $t('landing.hero.description') }}
            </p>

            <!-- 按钮组 - 增强动效 -->
            <div class="flex flex-col sm:flex-row justify-center gap-4 mb-12 animate-fade-in-up animation-delay-700">
              <Link :href="routes.pricing()" 
                    class="group relative bg-primary-500 hover:bg-primary-600 text-white px-10 py-4 rounded-xl transition-all duration-300 font-semibold shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 hover:-translate-y-1 overflow-hidden">
                <span class="relative z-10">{{ $t('landing.hero.cta') }}</span>
                <div class="absolute inset-0 bg-gradient-to-r from-primary-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
              
              <a href="#features" 
                 class="group px-10 py-4 rounded-xl border-2 border-gray-200 hover:border-primary-300 bg-white/80 backdrop-blur-sm hover:bg-white transition-all duration-300 text-gray-700 font-medium hover:-translate-y-1 hover:shadow-lg">
                <span class="flex items-center justify-center gap-2">
                  {{ $t('landing.hero.learnMore') }}
                  <i class="i-material-symbols-arrow-downward text-lg group-hover:animate-bounce"></i>
                </span>
              </a>
            </div>

            <!-- 技术标签云 - 带悬停动画 -->
            <div class="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto mt-10 mb-6">
              <div v-for="(tech, index) in technologies" :key="index"
                   class="tech-tag px-5 py-2.5 rounded-full bg-white/90 backdrop-blur-sm shadow-md text-sm font-medium text-gray-600 border border-gray-100/80 hover:border-primary-300 hover:text-primary-600 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default"
                   :style="{ animationDelay: (index * 80) + 'ms' }">
                {{ tech }}
              </div>
            </div>
            
            <!-- 免费试用提示 -->
            <div class="text-sm text-gray-500 mt-6 animate-fade-in-up animation-delay-1000 flex items-center justify-center gap-2">
              <i class="i-material-symbols-check-circle text-green-500"></i>
              无需信用卡，立即开始免费试用
            </div>
          </div>
        </div>
        
        <!-- 向下滚动提示 -->
        <div class="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow">
          <a href="#features" class="flex flex-col items-center text-gray-400 hover:text-primary-500 transition-colors duration-300">
            <span class="text-xs mb-2">向下滚动</span>
            <i class="i-material-symbols-keyboard-arrow-down text-2xl"></i>
          </a>
        </div>
      </section>

      <!-- 功能卡片区域 - 增强3D效果 -->
      <section class="py-20 bg-gradient-to-b from-gray-50 to-white" id="features">
        <div class="container mx-auto px-4">
          <div class="text-center mb-16 scroll-reveal">
            <div class="inline-flex items-center px-4 py-2 bg-primary-50 text-primary-600 text-sm font-medium rounded-full mb-6 border border-primary-100">
              <i class="i-material-symbols-star text-yellow-500 mr-2"></i>
              核心功能
            </div>
            <h2 class="text-3xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-primary-800 to-gray-900">
              {{ $t('landing.features.title') }}
            </h2>
            <p class="text-gray-600 max-w-2xl mx-auto text-lg">
              {{ $t('landing.features.description') }}
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <!-- 卡片1: AI Agent -->
            <div class="feature-card-3d scroll-reveal group bg-white rounded-2xl p-8 shadow-lg border border-gray-100/50 hover:border-primary-200 transition-all duration-500"
                 @mouseenter="handleCardHover($event, 'enter')"
                 @mousemove="handleCardMove"
                 @mouseleave="handleCardHover($event, 'leave')">
              <div class="relative">
                <div class="mb-6 w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg shadow-primary-500/30 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <i class="text-2xl text-white i-material-symbols-smart-toy"></i>
                </div>

                <h3 class="text-xl font-bold mb-3 text-gray-800 group-hover:text-primary-600 transition-colors">
                  {{ $t('landing.features.cards.agent.title') }}
                </h3>
                <p class="text-gray-600 mb-5 text-sm leading-relaxed">
                  {{ $t('landing.features.cards.agent.description') }}
                </p>

                <ul class="space-y-3 mb-6">
                  <li v-for="(feature, index) in agentFeatures" :key="index"
                      class="flex items-center text-sm text-gray-600 group-hover:translate-x-1 transition-transform duration-300"
                      :style="{ transitionDelay: (index * 50) + 'ms' }">
                    <i class="text-primary-500 i-material-symbols-check-circle mr-3 text-lg"></i>
                    {{ feature }}
                  </li>
                </ul>

                <a href="#" class="inline-flex items-center text-primary-500 hover:text-primary-600 text-sm font-semibold group/link">
                  {{ $t('landing.features.cards.agent.action') }}
                  <i class="ml-2 i-material-symbols-arrow-forward group-hover/link:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </div>

            <!-- 卡片2: 智能推理 -->
            <div class="feature-card-3d scroll-reveal group bg-white rounded-2xl p-8 shadow-lg border border-gray-100/50 hover:border-orange-200 transition-all duration-500"
                 style="animation-delay: 100ms"
                 @mouseenter="handleCardHover($event, 'enter')"
                 @mousemove="handleCardMove"
                 @mouseleave="handleCardHover($event, 'leave')">
              <div class="relative">
                <div class="mb-6 w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <i class="text-2xl text-white i-material-symbols-psychology"></i>
                </div>

                <h3 class="text-xl font-bold mb-3 text-gray-800 group-hover:text-orange-600 transition-colors">
                  {{ $t('landing.features.cards.reasoning.title') }}
                </h3>
                <p class="text-gray-600 mb-5 text-sm leading-relaxed">
                  {{ $t('landing.features.cards.reasoning.description') }}
                </p>

                <ul class="space-y-3 mb-6">
                  <li v-for="(feature, index) in reasoningFeatures" :key="index"
                      class="flex items-center text-sm text-gray-600 group-hover:translate-x-1 transition-transform duration-300"
                      :style="{ transitionDelay: (index * 50) + 'ms' }">
                    <i class="text-orange-500 i-material-symbols-check-circle mr-3 text-lg"></i>
                    {{ feature }}
                  </li>
                </ul>

                <a href="#" class="inline-flex items-center text-orange-500 hover:text-orange-600 text-sm font-semibold group/link">
                  {{ $t('landing.features.cards.reasoning.action') }}
                  <i class="ml-2 i-material-symbols-arrow-forward group-hover/link:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </div>

            <!-- 卡片3: 工具集成 -->
            <div class="feature-card-3d scroll-reveal group bg-white rounded-2xl p-8 shadow-lg border border-gray-100/50 hover:border-blue-200 transition-all duration-500"
                 style="animation-delay: 200ms"
                 @mouseenter="handleCardHover($event, 'enter')"
                 @mousemove="handleCardMove"
                 @mouseleave="handleCardHover($event, 'leave')">
              <div class="relative">
                <div class="mb-6 w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <i class="text-2xl text-white i-material-symbols-hub"></i>
                </div>

                <h3 class="text-xl font-bold mb-3 text-gray-800 group-hover:text-blue-600 transition-colors">
                  {{ $t('landing.features.cards.tools.title') }}
                </h3>
                <p class="text-gray-600 mb-5 text-sm leading-relaxed">
                  {{ $t('landing.features.cards.tools.description') }}
                </p>

                <ul class="space-y-3 mb-6">
                  <li v-for="(feature, index) in toolFeatures" :key="index"
                      class="flex items-center text-sm text-gray-600 group-hover:translate-x-1 transition-transform duration-300"
                      :style="{ transitionDelay: (index * 50) + 'ms' }">
                    <i class="text-blue-500 i-material-symbols-check-circle mr-3 text-lg"></i>
                    {{ feature }}
                  </li>
                </ul>

                <a href="#" class="inline-flex items-center text-blue-500 hover:text-blue-600 text-sm font-semibold group/link">
                  {{ $t('landing.features.cards.tools.action') }}
                  <i class="ml-2 i-material-symbols-arrow-forward group-hover/link:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 产品展示区 - 交错入场动画 -->
      <section class="py-20 bg-white relative overflow-hidden">
        <!-- 装饰性背景 -->
        <div class="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-primary-100/50 to-transparent rounded-full blur-3xl"></div>
        <div class="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-blue-100/50 to-transparent rounded-full blur-3xl"></div>
        <div class="container mx-auto px-4 relative">
          <div class="text-center mb-14 scroll-reveal">
            <div class="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-600 text-sm font-medium rounded-full mb-6 border border-blue-100">
              <i class="i-material-symbols-rocket-launch mr-2"></i>
              快速入门
            </div>
            <h2 class="text-3xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-blue-800 to-gray-900">
              {{ $t('landing.showcase.title') }}
            </h2>
            <p class="text-gray-600 max-w-2xl mx-auto text-lg">
              {{ $t('landing.showcase.description') }}
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <!-- 左侧卡片 1 -->
            <div class="showcase-card scroll-reveal bg-white border border-gray-100/80 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group">
              <div class="aspect-video relative overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100">
                <div class="absolute inset-0 flex items-center justify-center">
                  <div class="w-20 h-20 flex items-center justify-center rounded-2xl bg-white shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                    <i class="text-4xl text-blue-500 i-material-symbols-code group-hover:animate-pulse"></i>
                  </div>
                </div>
                <!-- 装饰性动画元素 -->
                <div class="absolute top-4 right-4 w-3 h-3 bg-blue-400 rounded-full animate-ping"></div>
                <div class="absolute bottom-4 left-4 w-2 h-2 bg-blue-300 rounded-full animate-bounce animation-delay-500"></div>
              </div>
              
              <div class="p-5">
                <div class="flex justify-between items-center mb-3">
                  <div class="inline-block bg-blue-50 text-blue-600 text-xs px-2 py-1 rounded-full">
                    {{ $t('landing.showcase.cases.coding.title') }}
                  </div>
                  <span class="text-xs text-gray-400">新手友好</span>
                </div>
                
                <!-- 简化的数据展示 -->
                <div class="space-y-2 mb-4">
                  <div class="flex items-center justify-between text-sm">
                    <span class="text-gray-500">{{ $t('landing.showcase.cases.coding.stats_title.task') }}</span>
                    <span class="font-medium">{{ $t('landing.showcase.cases.coding.stats_value.task') }}</span>
                  </div>
                  <div class="flex items-center justify-between text-sm">
                    <span class="text-gray-500">{{ $t('landing.showcase.cases.coding.stats_title.tools') }}</span>
                    <span class="font-medium">{{ $t('landing.showcase.cases.coding.stats_value.tools') }}</span>
                  </div>
                  <div class="flex items-center justify-between text-sm">
                    <span class="text-gray-500">{{ $t('landing.showcase.cases.coding.stats_title.efficiency') }}</span>
                    <span class="font-medium text-blue-500">{{ $t('landing.showcase.cases.coding.stats_value.efficiency') }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 中间卡片 -->
            <div class="showcase-card scroll-reveal bg-white border border-gray-100/80 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group" style="animation-delay: 150ms">
              <div class="aspect-video relative overflow-hidden bg-gradient-to-br from-primary-50 to-primary-100">
                <div class="absolute inset-0 flex items-center justify-center">
                  <div class="w-20 h-20 flex items-center justify-center rounded-2xl bg-white shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                    <i class="text-4xl text-primary-500 i-material-symbols-robot group-hover:animate-pulse"></i>
                  </div>
                </div>
                <div class="absolute top-4 left-4 w-3 h-3 bg-primary-400 rounded-full animate-ping animation-delay-300"></div>
                <div class="absolute bottom-4 right-4 w-2 h-2 bg-primary-300 rounded-full animate-bounce"></div>
              </div>
              
              <div class="p-5">
                <div class="flex justify-between items-center mb-3">
                  <div class="inline-block bg-primary-50 text-primary-600 text-xs px-2 py-1 rounded-full">
                    {{ $t('landing.showcase.cases.ask.title') }}
                  </div>
                  <span class="text-xs text-gray-400">智能问答</span>
                </div>
                
                <p class="text-sm text-gray-600 mb-4 line-clamp-2">{{ $t('landing.showcase.cases.ask.description') }}</p>
                
                <a href="#" class="inline-flex items-center text-primary-500 hover:text-primary-600 text-sm font-medium">
                  {{ $t('landing.showcase.cases.ask.action') }}
                  <i class="ml-1 i-material-symbols-arrow-forward-ios"></i>
                </a>
              </div>
            </div>

            <!-- 右侧卡片 -->
            <div class="showcase-card scroll-reveal bg-white border border-gray-100/80 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group" style="animation-delay: 300ms">
              <div class="aspect-video relative overflow-hidden bg-gradient-to-br from-cyan-50 to-cyan-100">
                <div class="absolute inset-0 flex items-center justify-center">
                  <div class="w-20 h-20 flex items-center justify-center rounded-2xl bg-white shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                    <i class="text-4xl text-cyan-500 i-material-symbols-analytics group-hover:animate-pulse"></i>
                  </div>
                </div>
                <div class="absolute top-4 right-4 w-3 h-3 bg-cyan-400 rounded-full animate-ping animation-delay-600"></div>
                <div class="absolute bottom-4 left-4 w-2 h-2 bg-cyan-300 rounded-full animate-bounce animation-delay-200"></div>
              </div>
              
              <div class="p-5">
                <div class="flex justify-between items-center mb-3">
                  <div class="inline-block bg-cyan-50 text-cyan-600 text-xs px-2 py-1 rounded-full">
                    {{ $t('landing.showcase.cases.analysis.title') }}
                  </div>
                  <span class="text-xs text-gray-400">数据分析</span>
                </div>
                
                <p class="text-sm text-gray-600 mb-4 line-clamp-2">{{ $t('landing.showcase.cases.analysis.description') }}</p>
                
                <a href="#" class="inline-flex items-center text-cyan-500 hover:text-cyan-600 text-sm font-medium">
                  {{ $t('landing.showcase.cases.analysis.action') }}
                  <i class="ml-1 i-material-symbols-arrow-forward-ios"></i>
                </a>
              </div>
            </div>
          </div>

          <!-- 底部案例 - 简洁风格 -->
          <div class="mt-12 pt-6 border-t border-gray-100">
            <h3 class="text-xl font-semibold mb-6 text-center">{{ $t('landing.showcase.more_cases') }}</h3>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- 左侧助手案例 -->
              <div class="bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow transition-shadow duration-300">
                <div class="flex p-4 border-b border-gray-50">
                  <div class="w-10 h-10 flex-shrink-0 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <i class="text-xl text-green-500 i-material-symbols-chat"></i>
                  </div>
                  <div>
                    <h4 class="font-medium">{{ $t('landing.showcase.cases.assistant.title') }}</h4>
                    <p class="text-xs text-gray-500">智能助手</p>
                  </div>
                </div>
                
                <div class="p-4">
                  <p class="text-sm text-gray-600 mb-3 line-clamp-2">{{ $t('landing.showcase.cases.assistant.description') }}</p>
                  
                  <!-- 简化的聊天气泡演示 -->
                  <div class="mb-4 border border-gray-100 rounded-lg p-3 bg-gray-50">
                    <div class="flex items-center mb-2">
                      <span class="w-6 h-6 bg-gray-200 rounded-full mr-2 flex-shrink-0"></span>
                      <p class="text-xs text-gray-600">{{ $t('landing.showcase.cases.assistant.user_message') }}</p>
                    </div>
                    <div class="flex items-center">
                      <span class="w-6 h-6 bg-primary-100 rounded-full mr-2 flex-shrink-0"></span>
                      <p class="text-xs text-gray-600">{{ $t('landing.showcase.cases.assistant.ai_response') }}</p>
                    </div>
                  </div>
                  
                  <div class="flex justify-between items-center">
                    <span class="text-xs text-gray-400">{{ $t('landing.showcase.cases.assistant.date') }}</span>
                    <a href="#" class="text-green-500 hover:text-green-600 text-sm">
                      {{ $t('landing.showcase.cases.assistant.action') }}
                      <i class="ml-1 i-material-symbols-arrow-forward-ios"></i>
                    </a>
                  </div>
                </div>
              </div>
              
              <!-- 右侧智能流程案例 -->
              <div class="bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow transition-shadow duration-300">
                <div class="flex p-4 border-b border-gray-50">
                  <div class="w-10 h-10 flex-shrink-0 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                    <i class="text-xl text-purple-500 i-material-symbols-data-process"></i>
                  </div>
                  <div>
                    <h4 class="font-medium">{{ $t('landing.showcase.cases.workflow.title') }}</h4>
                    <p class="text-xs text-gray-500">工作流</p>
                  </div>
                </div>
                
                <div class="p-4">
                  <p class="text-sm text-gray-600 mb-3 line-clamp-2">{{ $t('landing.showcase.cases.workflow.description') }}</p>
                  
                  <!-- 简化的流程演示 -->
                  <div class="mb-4 border border-gray-100 rounded-lg p-3 bg-gray-50">
                    <div class="flex justify-between items-center">
                      <div class="flex flex-col items-center">
                        <div class="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mb-1">
                          <i class="text-xs text-purple-500 i-material-symbols-input-circle"></i>
                        </div>
                        <span class="text-xs text-gray-500">输入</span>
                      </div>
                      <div class="h-1 flex-1 bg-gray-200 mx-2"></div>
                      <div class="flex flex-col items-center">
                        <div class="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mb-1">
                          <i class="text-xs text-purple-500 i-material-symbols-data-process"></i>
                        </div>
                        <span class="text-xs text-gray-500">处理</span>
                      </div>
                      <div class="h-1 flex-1 bg-gray-200 mx-2"></div>
                      <div class="flex flex-col items-center">
                        <div class="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mb-1">
                          <i class="text-xs text-purple-500 i-material-symbols-output"></i>
                        </div>
                        <span class="text-xs text-gray-500">输出</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="flex justify-between items-center">
                    <span class="text-xs text-gray-400">{{ $t('landing.showcase.cases.workflow.date') }}</span>
                    <a href="#" class="text-purple-500 hover:text-purple-600 text-sm">
                      {{ $t('landing.showcase.cases.workflow.action') }}
                      <i class="ml-1 i-material-symbols-arrow-forward-ios"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- FAQ区域 -->
      <section class="relative py-20">
        <div class="absolute inset-0 bg-gradient-to-b from-white via-gray-50/50 to-white"></div>
        <div class="container mx-auto px-4 relative">
          <h2
            class="text-3xl font-bold text-center mb-12 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700">
            {{ $t('landing.faq.title') }}
          </h2>
          <div class="max-w-3xl mx-auto space-y-6">
            <div v-for="(faq, index) in faqs" :key="index"
              class="group bg-white/80 backdrop-blur-sm rounded-lg shadow-lg overflow-hidden transition-all duration-300 border border-gray-100/50"
              :class="{ 'shadow-xl': activeFaq === index }">
              <button @click="toggleFaq(index)"
                class="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50/50 transition-colors">
                <h3
                  class="text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700">
                  {{
                    faq.question }}</h3>
                <div
                  class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-pink-100 transition-colors">
                  <svg
                    class="w-5 h-5 text-gray-500 group-hover:text-pink-500 transform transition-transform duration-300"
                    :class="{ 'rotate-180': activeFaq === index }" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              <div class="overflow-hidden transition-all duration-300 bg-gradient-to-b from-transparent to-gray-50/30"
                :class="{ 'max-h-96': activeFaq === index, 'max-h-0': activeFaq !== index }"
                :style="{ opacity: activeFaq === index ? 1 : 0 }">
                <div class="p-6 prose prose-gray max-w-none">
                  <p class="text-gray-600">{{ faq.answer }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  </div>
</template>

<script setup>
import { Link } from '@inertiajs/vue3'
import LandingHeader from '@/components/layout/LandingHeader.vue'
import LandingFooter from '@/components/layout/LandingFooter.vue'

const { t } = useI18n()

// FAQ数据
const faqs = [
  {
    question: t('landing.faq.questions.pricing.question'),
    answer: t('landing.faq.questions.pricing.answer')
  },
  {
    question: t('landing.faq.questions.howToStart.question'),
    answer: t('landing.faq.questions.howToStart.answer')
  },
  {
    question: t('landing.faq.questions.platforms.question'),
    answer: t('landing.faq.questions.platforms.answer')
  }
]

// 切换FAQ展开状态
const activeFaq = ref(null)
const toggleFaq = (index) => {
  activeFaq.value = activeFaq.value === index ? null : index
}

// 案例数据
const cases = [
  {
    id: 1,
    title: t('landing.showcase.cases.coding.title'),
    description: t('landing.showcase.cases.coding.description'),
    stats: t('landing.showcase.cases.coding.stats'),
    performance: t('landing.showcase.cases.coding.performance')
  },
  {
    id: 2,
    title: t('landing.showcase.cases.analysis.title'),
    description: t('landing.showcase.cases.analysis.description'),
    stats: t('landing.showcase.cases.analysis.stats'),
    performance: t('landing.showcase.cases.analysis.performance')
  },
  {
    id: 3,
    title: t('landing.showcase.cases.assistant.title'),
    description: t('landing.showcase.cases.assistant.description'),
    stats: t('landing.showcase.cases.assistant.stats'),
    performance: t('landing.showcase.cases.assistant.performance')
  },
  {
    id: 4,
    title: t('landing.showcase.cases.automation.title'),
    description: t('landing.showcase.cases.automation.description'),
    stats: t('landing.showcase.cases.automation.stats'),
    performance: t('landing.showcase.cases.automation.performance')
  }
]

// 滚动相关
const scrollContainer = ref(null)
const scrollContent = ref(null)
const scrollPosition = ref(0)
const scrolling = ref(true)
let scrollAnimation = null

// 暂停滚动
const pauseScroll = () => {
  scrolling.value = false
  if (scrollAnimation) {
    cancelAnimationFrame(scrollAnimation)
    scrollAnimation = null
  }
}

// 恢复滚动
const resumeScroll = () => {
  if (!scrolling.value) {
    scrolling.value = true
    startScroll()
  }
}

// 开始滚动动画
const startScroll = () => {
  const animate = () => {
    if (!scrolling.value) return

    scrollPosition.value -= 1.5 // 调整滚动速度
    const contentWidth = scrollContent.value?.offsetWidth || 0
    const halfContentWidth = contentWidth / 2

    // 当滚动到一半时重置位置
    if (Math.abs(scrollPosition.value) >= halfContentWidth) {
      scrollPosition.value = 0
    }
    scrollAnimation = requestAnimationFrame(animate)
  }
  scrollAnimation = requestAnimationFrame(animate)
}

// 滚动动画观察器
let scrollObserver = null

// 3D卡片效果
const handleCardHover = (event, action) => {
  const card = event.currentTarget
  if (action === 'enter') {
    card.style.transition = 'transform 0.1s ease-out'
  } else {
    card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)'
    card.style.transition = 'transform 0.5s ease-out'
  }
}

const handleCardMove = (event) => {
  const card = event.currentTarget
  const rect = card.getBoundingClientRect()
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top
  const centerX = rect.width / 2
  const centerY = rect.height / 2
  const rotateX = (y - centerY) / 20
  const rotateY = (centerX - x) / 20
  card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`
}

// 初始化滚动动画
const initScrollAnimations = () => {
  scrollObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('scroll-visible')
      }
    })
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  })

  document.querySelectorAll('.scroll-reveal').forEach(el => {
    scrollObserver.observe(el)
  })
}

// 组件挂载时启动
onMounted(() => {
  startScroll()
  nextTick(() => {
    initScrollAnimations()
  })
})

// 组件卸载时清理
onUnmounted(() => {
  if (scrollAnimation) {
    cancelAnimationFrame(scrollAnimation)
  }
  if (scrollObserver) {
    scrollObserver.disconnect()
  }
})

// 广告案例数据
const adCases = ref([
  {
    title: t('landing.showcase.cases.coding.title'),
    description: t('landing.showcase.cases.coding.description'),
    stats: t('landing.showcase.cases.coding.stats'),
    performance: t('landing.showcase.cases.coding.performance'),
  },
  {
    title: t('landing.showcase.cases.analysis.title'),
    description: t('landing.showcase.cases.analysis.description'),
    stats: t('landing.showcase.cases.analysis.stats'),
    performance: t('landing.showcase.cases.analysis.performance'),
  },
  {
    title: t('landing.showcase.cases.assistant.title'),
    description: t('landing.showcase.cases.assistant.description'),
    stats: t('landing.showcase.cases.assistant.stats'),
    performance: t('landing.showcase.cases.assistant.performance'),
  },
  {
    title: t('landing.showcase.cases.automation.title'),
    description: t('landing.showcase.cases.automation.description'),
    stats: t('landing.showcase.cases.automation.stats'),
    performance: t('landing.showcase.cases.automation.performance'),
  },
])

// 技术标签云数据
const technologies = [
  'Ruby on Rails',
  'Vue 3',
  'Inertia.js',
  'PostgreSQL',
  'Tailwind CSS',
  'Element Plus',
  'AI Agent',
  'LLM',
  'Automation',
  'Cloud Native'
]

// 功能特性数据
const agentFeatures = [
  t('landing.features.cards.agent.features.customizable'),
  t('landing.features.cards.agent.features.multilingual'),
  t('landing.features.cards.agent.features.scalable')
]

const reasoningFeatures = [
  t('landing.features.cards.reasoning.features.contextual'),
  t('landing.features.cards.reasoning.features.accurate'),
  t('landing.features.cards.reasoning.features.efficient')
]

const toolFeatures = [
  t('landing.features.cards.tools.features.extensive'),
  t('landing.features.cards.tools.features.flexible'),
  t('landing.features.cards.tools.features.secure')
]

// 分析图表数据
const analyticsBars = ref([60, 80, 40, 70, 50])

// 聊天消息数据
const chatMessages = [
  { type: 'user', content: t('landing.showcase.cases.assistant.messages.user1') },
  { type: 'assistant', content: t('landing.showcase.cases.assistant.messages.assistant1') },
  { type: 'user', content: t('landing.showcase.cases.assistant.messages.user2') },
  { type: 'assistant', content: t('landing.showcase.cases.assistant.messages.assistant2') }
]

// 自动化节点数据
const automationNodes = [
  { x: 20, y: 20, icon: 'i-material-symbols-input' },
  { x: 50, y: 50, icon: 'i-material-symbols-autorenew' },
  { x: 80, y: 80, icon: 'i-material-symbols-output' }
]
</script>

<style>
.bg-gradient-animate {
  background-size: 400% 400%;
  animation: gradient 15s ease infinite;
}

@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

/* 文字渐变动画 */
.animate-text-gradient {
  background: linear-gradient(to right, #3b82f6, #ec4899, #3b82f6);
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  animation: text-gradient 3s linear infinite;
}

@keyframes text-gradient {
  to {
    background-position: 200% center;
  }
}

/* 淡入上升动画 */
.animate-fade-in-up {
  animation: fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  opacity: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 淡入下降动画 */
.animate-fade-in-down {
  animation: fadeInDown 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  opacity: 0;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 渐变X轴动画 */
.animate-gradient-x {
  animation: gradientX 6s ease infinite;
}

@keyframes gradientX {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.bg-size-200 {
  background-size: 200% 200%;
}

/* 慢速弹跳 */
.animate-bounce-slow {
  animation: bounceSlow 2s ease-in-out infinite;
}

@keyframes bounceSlow {
  0%, 100% {
    transform: translateY(0) translateX(-50%);
  }
  50% {
    transform: translateY(-10px) translateX(-50%);
  }
}

/* 慢速闪烁 */
.animate-ping-slow {
  animation: pingSlow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
}

@keyframes pingSlow {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  75%, 100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

/* 粒子浮动 */
.particle {
  animation: particleFloat 20s ease-in-out infinite;
}

@keyframes particleFloat {
  0%, 100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.3;
  }
  25% {
    transform: translate(30px, -50px) scale(1.2);
    opacity: 0.5;
  }
  50% {
    transform: translate(-20px, -100px) scale(0.8);
    opacity: 0.4;
  }
  75% {
    transform: translate(50px, -50px) scale(1.1);
    opacity: 0.6;
  }
}

/* 形态变换动画 */
.animate-morph {
  animation: morph 15s ease-in-out infinite;
}

@keyframes morph {
  0%, 100% {
    border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
    transform: rotate(0deg) scale(1);
  }
  25% {
    border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%;
  }
  50% {
    border-radius: 50% 60% 30% 60% / 30% 40% 70% 50%;
    transform: rotate(180deg) scale(1.1);
  }
  75% {
    border-radius: 60% 40% 50% 40% / 60% 50% 40% 60%;
  }
}

/* 脉冲发光 */
.animate-pulse-glow {
  animation: pulseGlow 4s ease-in-out infinite;
}

@keyframes pulseGlow {
  0%, 100% {
    opacity: 0.3;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 0.6;
    transform: translate(-50%, -50%) scale(1.2);
  }
}

/* 技术标签动画 */
.tech-tag {
  animation: techTagIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  opacity: 0;
  transform: translateY(20px) scale(0.9);
}

@keyframes techTagIn {
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 3D卡片样式 */
.feature-card-3d {
  transform-style: preserve-3d;
  will-change: transform;
}

/* 动画延迟类 */
.animation-delay-200 {
  animation-delay: 200ms;
}

.animation-delay-400 {
  animation-delay: 400ms;
}

.animation-delay-600 {
  animation-delay: 600ms;
}

.animation-delay-4000 {
  animation-delay: 4000ms;
}

/* 滚动显示动画 */
.scroll-reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.scroll-reveal.scroll-visible {
  opacity: 1;
  transform: translateY(0);
}

/* 滚动时的动画效果 */
.scroll-animate {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s ease-out;
}

.scroll-animate.visible {
  opacity: 1;
  transform: translateY(0);
}

/* 悬浮动画增强 */
.hover\:scale-up {
  transition: transform 0.3s ease;
}

.hover\:scale-up:hover {
  transform: scale(1.05);
}

/* 装饰性背景动画 */
.footer-gradient {
  background-size: 400% 400%;
  animation: footer-gradient 15s ease infinite;
}

@keyframes footer-gradient {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

/* 按钮动画 */
.btn-hover {
  transition: all 0.3s ease;
}

.btn-hover:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(236, 72, 153, 0.2);
}

/* 图标动画 */
@keyframes iconSpin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.icon-spin:hover {
  animation: iconSpin 1s linear infinite;
}

/* 技术关键词动画 */
@keyframes float-up {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }

  100% {
    opacity: 0.1;
    transform: translateY(0);
  }
}

.tech-keyword {
  animation: float-up 1s ease-out forwards;
  opacity: 0;
}

.tech-keyword:nth-child(1) {
  animation-delay: 0.2s;
}

.tech-keyword:nth-child(2) {
  animation-delay: 0.4s;
}

.tech-keyword:nth-child(3) {
  animation-delay: 0.6s;
}

.tech-keyword:nth-child(4) {
  animation-delay: 0.8s;
}

/* 页脚链接悬停效果 */
.footer-link {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.footer-link::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 1px;
  background: linear-gradient(to right, theme('colors.primary.500'), theme('colors.primary.600'));
  transition: width 0.3s ease;
}

.footer-link:hover::after {
  width: 100%;
}

/* 页脚背景渐变动画 */
@keyframes gradientFlow {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

.footer-gradient {
  background-size: 200% 200%;
  animation: gradientFlow 15s ease infinite;
}

/* 新增动画效果 */
@keyframes slow-float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(5deg);
  }
}

@keyframes slow-pulse {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.1);
  }
}

@keyframes footer-bg {
  0% {
    background-position: 0% 0%;
  }
  100% {
    background-position: 100% 100%;
  }
}

@keyframes particle-float {
  0%, 100% {
    transform: translateY(0px) translateX(0px);
  }
  25% {
    transform: translateY(-10px) translateX(5px);
  }
  50% {
    transform: translateY(-5px) translateX(-5px);
  }
  75% {
    transform: translateY(-15px) translateX(3px);
  }
}

@keyframes glow-pulse {
  0%, 100% {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
  }
  50% {
    box-shadow: 0 0 40px rgba(59, 130, 246, 0.6), 0 0 60px rgba(59, 130, 246, 0.3);
  }
}

@keyframes text-shimmer {
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
}

@keyframes card-hover-glow {
  0% {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
  100% {
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 0 30px rgba(59, 130, 246, 0.1);
  }
}

/* 动画类 */
.animate-slow-float {
  animation: slow-float 6s ease-in-out infinite;
}

.animate-slow-pulse {
  animation: slow-pulse 4s ease-in-out infinite;
}

.animate-footer-bg {
  animation: footer-bg 20s linear infinite;
}

.animate-particle-float {
  animation: particle-float 8s ease-in-out infinite;
}

.animate-glow-pulse {
  animation: glow-pulse 3s ease-in-out infinite;
}

.animate-text-shimmer {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent);
  background-size: 200% 100%;
  animation: text-shimmer 3s ease-in-out infinite;
}

.feature-card:hover {
  animation: card-hover-glow 0.3s ease-out forwards;
}

.showcase-card:hover {
  animation: card-hover-glow 0.3s ease-out forwards;
}

/* 增强的动画延迟类 */
.animation-delay-100 {
  animation-delay: 100ms;
}

.animation-delay-300 {
  animation-delay: 300ms;
}

.animation-delay-500 {
  animation-delay: 500ms;
}

.animation-delay-700 {
  animation-delay: 700ms;
}

.animation-delay-900 {
  animation-delay: 900ms;
}

.animation-delay-1000 {
  animation-delay: 1000ms;
}

/* 响应式优化 */
@media (max-width: 768px) {
  .feature-card, .showcase-card {
    transform: none !important;
  }
  
  .feature-card:hover, .showcase-card:hover {
    transform: translateY(-2px) !important;
  }
  
  .animate-slow-float, .animate-slow-pulse {
    animation: none;
  }
}

/* 减少动画对性能的影响 */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

</style>
